/**
 * Single source of truth for password complexity.
 *
 * Use validatePassword() for any place where a *new* password is being
 * accepted (signup, reset, change). Login does NOT enforce this — legacy
 * users may have shorter passwords on file, and only Supabase can decide
 * whether the submitted password matches.
 */

export const PASSWORD_MIN_LENGTH = 12

export const PASSWORD_REQUIREMENTS_TEXT =
  "At least 12 characters with uppercase, lowercase, number, and symbol."

// `code` keys the localized message in messages/pages/auth.*.json under
// "passwordPolicy.<code>"; `error` stays English for server-side callers.
export type PasswordPolicyErrorCode =
  | "required"
  | "tooShort"
  | "tooLong"
  | "lowercase"
  | "uppercase"
  | "number"
  | "symbol"

export type PasswordValidation =
  | { ok: true }
  | { ok: false; error: string; code: PasswordPolicyErrorCode }

export function validatePassword(pw: unknown): PasswordValidation {
  if (typeof pw !== "string") {
    return { ok: false, error: "Password is required.", code: "required" }
  }
  if (pw.length < PASSWORD_MIN_LENGTH) {
    return { ok: false, error: `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`, code: "tooShort" }
  }
  if (pw.length > 128) {
    return { ok: false, error: "Password must be 128 characters or fewer.", code: "tooLong" }
  }
  if (!/[a-z]/.test(pw)) {
    return { ok: false, error: "Password must include a lowercase letter.", code: "lowercase" }
  }
  if (!/[A-Z]/.test(pw)) {
    return { ok: false, error: "Password must include an uppercase letter.", code: "uppercase" }
  }
  if (!/[0-9]/.test(pw)) {
    return { ok: false, error: "Password must include a number.", code: "number" }
  }
  if (!/[^A-Za-z0-9]/.test(pw)) {
    return { ok: false, error: "Password must include a symbol.", code: "symbol" }
  }
  return { ok: true }
}

/**
 * react-hook-form validate adapter. Returns true on success or the
 * error string on failure — matches RHF's expected signature.
 */
export function passwordRhfValidator(pw: string): true | string {
  const result = validatePassword(pw)
  return result.ok ? true : result.error
}
